// ============================================================
// CLIPBOARD - copy a piece of map, and stamp it again.
//
// A bunker is twenty drags. A row of four bunkers should not be eighty.
//
// A STAMP is a region lifted out of the map and made relative to its own
// corner: cubes at every height, the zones painted across it, and any
// trenches dug into it. Everything in the footprint, because "copy this
// bit of map" means the bit of map, not the bit of map minus the hazard
// someone painted on it.
//
// Mirroring and rotating are transforms on the stamp rather than on the
// map, so they cost nothing until it is pasted and can be undone by
// mirroring again. Pasting is `setCells` and `dig`, which already exist:
// nothing downstream learns a new word.
//
// Pure. Zero React dependency.
// ============================================================
import type { VoxelGrid } from './voxels';
import { cellKey, parseCell, setCells } from './voxels';
import type { Zone } from './zones';
import { makeZone, oneSurfacePerCell, zoneCellKey, parseZoneCell } from './zones';
import { dig } from './excavation';

/** One cube in a stamp, relative to the stamp's own corner. */
interface StampCell {
  dx: number;
  dy: number;
  dz: number;
  material: string;
}

/** A patch of painted ground in a stamp. */
interface StampZone {
  going: Zone['going'];
  hazard: boolean;
  colour: string;
  label?: string;
  hidden?: boolean;
  motion?: Zone['motion'];
  light?: true;
  cells: { dx: number; dz: number }[];
}

export interface Stamp {
  /** Extent in cells, so a mirror knows what to reflect about. */
  width: number;
  depth: number;
  cells: StampCell[];
  /** Dug cells, relative. `dy` is negative, as it is everywhere else. */
  /** The holes, each with its lining when it is not earth (engine/battlefield/excavation). */
  dug: { dx: number; dy: number; dz: number; material?: string }[];
  zones: StampZone[];
}

/** The inclusive cell rectangle between two corners. */
function bounds(a: { x: number; z: number }, b: { x: number; z: number }) {
  return {
    x0: Math.min(a.x, b.x),
    x1: Math.max(a.x, b.x),
    z0: Math.min(a.z, b.z),
    z1: Math.max(a.z, b.z),
  };
}

/**
 * Lift a region out of the map.
 *
 * Everything whose x,z falls in the rectangle, at every height - a
 * building is not a footprint, and copying only the ground floor would
 * be a strange thing to offer.
 */
export function copyRegion(
  grid: VoxelGrid | undefined,
  zones: Zone[] | undefined,
  from: { x: number; z: number },
  to: { x: number; z: number },
): Stamp {
  const box = bounds(from, to);
  return {
    width: box.x1 - box.x0 + 1,
    depth: box.z1 - box.z0 + 1,
    cells: Object.entries(grid?.cells ?? {}).flatMap(([key, material]) => {
      const at = relativeTo(box, key);
      return at ? [{ ...at, material }] : [];
    }),
    dug: Object.entries(grid?.dug ?? {}).flatMap(([key, lining]) => {
      const at = relativeTo(box, key);
      if (!at) return [];
      return [lining === true ? at : { ...at, material: lining }];
    }),
    zones: (zones ?? []).flatMap((zone) => stampZone(zone, box)),
  };
}

type Box = ReturnType<typeof bounds>;

function insideBox(box: Box, x: number, z: number): boolean {
  return x >= box.x0 && x <= box.x1 && z >= box.z0 && z <= box.z1;
}

/** A voxel key relative to the box's corner, or null when it lies outside the box. */
function relativeTo(box: Box, key: string): { dx: number; dy: number; dz: number } | null {
  const { x, y, z } = parseCell(key);
  return insideBox(box, x, z) ? { dx: x - box.x0, dy: y, dz: z - box.z0 } : null;
}

/** The part of a zone inside the box, as a stamp zone - or nothing, when none of it is. */
function stampZone(zone: Zone, box: Box): StampZone[] {
  const kept = zone.cells
    .map(parseZoneCell)
    .filter((c) => insideBox(box, c.x, c.z))
    .map((c) => ({ dx: c.x - box.x0, dz: c.z - box.z0 }));
  return kept.length ? [{ ...zoneLooks(zone), cells: kept }] : [];
}

/** What a zone looks like and does, without its cells - what travels in a stamp. */
function zoneLooks(zone: Pick<StampZone, 'going' | 'hazard' | 'colour' | 'label' | 'hidden' | 'motion' | 'light'>) {
  return {
    going: zone.going,
    hazard: zone.hazard,
    colour: zone.colour,
    ...(zone.label ? { label: zone.label } : {}),
    ...(zone.hidden ? { hidden: true } : {}),
    ...(zone.motion ? { motion: zone.motion } : {}),
    ...(zone.light ? { light: true as const } : {}),
  };
}

/**
 * Flip a stamp about its own middle.
 *
 * Its own INVERSE: mirroring twice is the identity, which is what makes
 * it safe to offer as a button rather than as a decision.
 */
export function mirrorStamp(stamp: Stamp, axis: 'x' | 'z'): Stamp {
  const flipX = (dx: number) => stamp.width - 1 - dx;
  const flipZ = (dz: number) => stamp.depth - 1 - dz;
  const move = <T extends { dx: number; dz: number }>(c: T): T =>
    axis === 'x' ? { ...c, dx: flipX(c.dx) } : { ...c, dz: flipZ(c.dz) };

  return {
    ...stamp,
    cells: stamp.cells.map(move),
    dug: stamp.dug.map(move),
    zones: stamp.zones.map((zone) => ({ ...zone, cells: zone.cells.map(move) })),
  };
}

/**
 * Turn a stamp a quarter turn clockwise.
 *
 * Width and depth swap, which is why this cannot be expressed as two
 * mirrors and has to know the extent.
 */
export function rotateStamp(stamp: Stamp): Stamp {
  const turn = <T extends { dx: number; dz: number }>(c: T): T => ({ ...c, dx: stamp.depth - 1 - c.dz, dz: c.dx }) as T;

  return {
    width: stamp.depth,
    depth: stamp.width,
    cells: stamp.cells.map(turn),
    dug: stamp.dug.map(turn),
    zones: stamp.zones.map((zone) => ({ ...zone, cells: zone.cells.map(turn) })),
  };
}

/** Is there anything in this stamp at all? */
export function stampIsEmpty(stamp: Stamp): boolean {
  return !stamp.cells.length && !stamp.dug.length && !stamp.zones.length;
}

/**
 * Stamp a region down with its corner at `at`.
 *
 * Additive: cubes overwrite what is under them, zones are added beside
 * whatever was already painted, and digging deepens rather than filling.
 * Paste over your own copy and you get back exactly what you copied.
 */
export function pasteStamp(
  grid: VoxelGrid,
  zones: Zone[],
  stamp: Stamp,
  at: { x: number; z: number },
): { voxels: VoxelGrid; zones: Zone[] } {
  const voxels = pasteDug(pasteCubes(grid, stamp, at), stamp, at);
  const pasted = stamp.zones.map((zone) =>
    makeZone({ ...zoneLooks(zone), cells: zone.cells.map((c) => zoneCellKey(at.x + c.dx, at.z + c.dz)) }),
  );
  if (!pasted.length) return { voxels, zones };
  // The paste is painted, so it carves what it lands on: one surface per cell, the paste laid down last.
  return { voxels, zones: oneSurfacePerCell([...zones, ...pasted]) ?? pasted };
}

/**
 * The stamp's cubes, laid down at `at`. One pass per material, so
 * `setCells` is called a handful of times rather than once per cube.
 */
function pasteCubes(grid: VoxelGrid, stamp: Stamp, at: { x: number; z: number }): VoxelGrid {
  const byMaterial = new Map<string, string[]>();
  for (const cell of stamp.cells) {
    const key = cellKey(at.x + cell.dx, cell.dy, at.z + cell.dz);
    const list = byMaterial.get(cell.material);
    if (list) list.push(key);
    else byMaterial.set(cell.material, [key]);
  }
  let voxels = grid;
  for (const [material, keys] of byMaterial) voxels = setCells(voxels, keys, material);
  return voxels;
}

/** The stamp's excavation, dug at `at`; the grid itself when the stamp dug nothing. */
function pasteDug(voxels: VoxelGrid, stamp: Stamp, at: { x: number; z: number }): VoxelGrid {
  if (!stamp.dug.length) return voxels;
  // One pass per lining, like the cubes: `dig` copies the excavation each call.
  const byLining = new Map<string | undefined, string[]>();
  for (const c of stamp.dug) {
    const keys = byLining.get(c.material) ?? [];
    keys.push(cellKey(at.x + c.dx, c.dy, at.z + c.dz));
    byLining.set(c.material, keys);
  }
  let dug = voxels.dug;
  for (const [material, keys] of byLining) dug = dig(dug, keys, material);
  return { ...voxels, dug };
}
